import { InfiniteData, Query, useQueryClient } from "@tanstack/react-query";
import { ActivitiesCollection } from "PFCore/hooks/queries/activities/query_keys";
import { MatchingActivitiesResponse } from "PFCore/services/matching_activities/fetch_matching_activities";
import { Id, IMatchScores, MatchedActivity } from "PFTypes";

export const useMatchingActivityScoresReplace = () => {
  const queryClient = useQueryClient();

  // After `matching_activities/${activityId}/refresh` endpoint is called,
  // the activity disappears from matching_activities temporarily (or permanently).
  // This function changes the score of this role in all the cached matching_activities queries
  // to make the role update and not disappear.
  // The known issue is that user may be missing one random role after fetching next page,
  // because the refreshed role disappears temporarily from API (so all the roles shift in the order).
  const replaceMatchingActivityScoresInCache = (roleId: Id, scores: IMatchScores | null) => {
    const matchingActivitiesQueries = queryClient.getQueryCache().findAll([], {
      predicate: (query: Query<InfiniteData<MatchingActivitiesResponse>>) =>
        query.queryKey.includes(ActivitiesCollection.MatchingActivities) && !!query.state.data?.pages
    }) as Query<InfiniteData<MatchingActivitiesResponse>>[];

    matchingActivitiesQueries.forEach((query) => {
      const queryData = { ...query.state.data };
      const changedPages = (queryData?.pages || []).map((pageData) => {
        const changedEntries = pageData.entries.map((matchedActivity: MatchedActivity) => {
          if (matchedActivity.id === roleId) {
            return {
              ...matchedActivity,
              scores
            };
          }

          return matchedActivity;
        });

        return { ...pageData, entries: changedEntries };
      });

      const changedQueryData = {
        ...queryData,
        pages: changedPages
      } as InfiniteData<MatchingActivitiesResponse>;

      query.setData(changedQueryData);
    });
  };

  return {
    replaceMatchingActivityScoresInCache
  };
};
